import { Box } from "@chakra-ui/react";
import { RemountOnReportOpen } from "../common/saved-reports/RemountOnReportOpen";
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { Home } from "./home/Home";
import { OverviewPage } from "./overview/OverviewPage";
import { NotificationsPage } from "./notifications/NotificationsPage";
import { AssistantPage } from "../common/agent/AssistantPage";
import { Admin } from "./admin";
import { urls } from "../urls";
import { DraftCard } from "../common/layout/DraftCard";
import { MarketDialoguePage } from "./market-dialogues/MarketDialoguePage";
import { MarketDialoguesList } from "./market-dialogues/MarketDialoguesList";
import { SupplierMarketDialoguePage } from "./market-dialogues/supplier/SupplierMarketDialoguePage";
import type { SectionTitle } from "../common/page-title/PageTitle";
import { EditContract } from "./contracts/edit/EditContract";
import { ContractSearchPage } from "./contracts/search/ContractSearchPage";
import { Contracts } from "./contracts/view-all/Contracts";
import { ActiveContractsRedirect } from "./contracts/view-all/ActiveContractsRedirect";
import { ContractTasks } from "./contracts/view-all-tasks/ContractTasks";
import { ContractPage } from "./contracts/view-single/ContractPage";
import { AccessManagementPage } from "./organizations/current/manage-users/AccessManagementPage";
import { EditOrganizationProfile } from "./organizations/current/edit/EditOrganizationProfile";
import { OrganizationsPage } from "./organizations/search/OrganizationsPage";
import { ViewOrganizationProfile } from "./organizations/view-single/ViewOrganizationProfile";
import { Projects } from "./projects/view-all/Projects";
import { ProjectPage } from "./projects/view-single/ProjectPage";
import { EditSourcingEventWizard } from "./sourcing-events/buyer/wizard/EditSourcingEventWizard";
import { LegacyWizardRedirect } from "./sourcing-events/buyer/wizard/LegacyWizardRedirect";
import { EditBidPage } from "./sourcing-events/portal/bid/EditBidPage";
import { EditPrivateBidPage } from "./sourcing-events/portal/bid/EditPrivateBidPage";
import { EditParticipationRequestPage } from "./sourcing-events/portal/request/EditParticipationRequestPage";
import { PortalSourcingEventsPage } from "./sourcing-events/portal/view-all/PortalSourcingEventsPage";
import { PortalSourcingEventPage } from "./sourcing-events/portal/view-single/PortalSourcingEventPage";
import { SupplierBidsList } from "./supplier/SupplierBidsList";
import { SupplierInvitationsList } from "./supplier/SupplierInvitationsList";
import { ContributingEntityPage } from "./sourcing-events/portal/contributing/ContributingEntityPage";
import { ContributingEntityResponsePage } from "./sourcing-events/portal/contributing/ContributingEntityResponsePage";
import { SupplierContributionsList } from "./sourcing-events/portal/contributing/SupplierContributionsList";
import { Behovsliste } from "./behov/Behovsliste";
import { NeedsRoute } from "./behov/NeedsRoute";
import { EditNeed } from "./behov/EditNeed";
import { MineBehov } from "./behov/MineBehov";
import { NeedView } from "./behov/NeedView";
import { NewNeed } from "./behov/NewNeed";
import { EditProfilePage } from "./profile/EditProfilePage";
import { ProfilePage } from "./profile/ProfilePage";
import { SettingsPage } from "./settings/SettingsPage";
import { Kravbibliotek } from "./kravbibliotek/Kravbibliotek";
import { MalEditor } from "./maler/MalEditor";
import { SourcingEventTemplates } from "./maler/SourcingEventTemplates";
import { SourcingEventsList } from "./SourcingEventsList";
import { MyReports } from "./saved-reports/MyReports";
import { BidDetailView } from "./sourcing-event/bid-detail/BidDetailView";
import { EditNegotiationRoundWizard } from "./sourcing-event/negotiation/wizard/EditNegotiationRoundWizard";
import { RequestDetailView } from "./sourcing-event/qualification/detail/RequestDetailView";
import { ProtocolPage } from "./sourcing-event/protocol/ProtocolPage";
import { SourcingEventView } from "./sourcing-event/SourcingEventView";
import { FrameworkAgreementPage } from "./framework-agreement/FrameworkAgreementPage";
import { FrameworkAgreementsList } from "./framework-agreement/FrameworkAgreementsList";
import { SupplierFrameworkAgreementsList } from "./supplier/SupplierFrameworkAgreementsList";

type AppShellRoute = {
  path: string;
  title: SectionTitle;
  element: ReactElement;
  fullWidth?: boolean;
  fillHeight?: boolean;
};

const contributingEntityRoutes: AppShellRoute[] = [
  { path: urls.app.supplier.contributions, title: (t) => t("My contributions"), element: <SupplierContributionsList />, fullWidth: true },
  { path: urls.app.supplier.contribution.pathname, title: (t) => t("My contributions"), element: <ContributingEntityPage /> },
  {
    path: urls.app.supplier.contributionReview.pathname, title: (t) => t("My contributions"),
    element: <ContributingEntityResponsePage />,
  },
];

export const appShellRoutes: AppShellRoute[] = [
  { path: urls.admin, title: (t) => t("Admin dashboard"), element: <Admin />, fullWidth: true },
  { path: urls.app.home, title: (t) => t("Home"), element: <Home /> },
  { path: urls.app.overview, title: (t) => t("Overview"), element: <OverviewPage /> },
  { path: urls.app.notifications, title: (t) => t("Alerts"), element: <NotificationsPage /> },
  {
    path: urls.app.assistant,
    title: (t) => t("Assistant"),
    element: <AssistantPage />,
    fullWidth: true,
    fillHeight: true,
  },
  { path: urls.app.needs.new, title: (t) => t("New need"), element: <NeedsRoute><NewNeed /></NeedsRoute> },
  { path: urls.app.needs.edit.pathname, title: (t) => t("Needs"), element: <NeedsRoute><EditNeed /></NeedsRoute> },
  { path: urls.app.needs.view.pathname, title: (t) => t("Needs"), element: <NeedsRoute><NeedView /></NeedsRoute> },
  { path: urls.app.needs.mine, title: (t) => t("My needs"), element: <NeedsRoute><MineBehov /></NeedsRoute>, fullWidth: true },
  { path: urls.app.needs.list, title: (t) => t("Needs overview"), element: <NeedsRoute><Behovsliste /></NeedsRoute>, fullWidth: true },
  { path: urls.app.sourcingEvents.viewAll, title: (t) => t("Procurements"), element: (
      <RemountOnReportOpen>
        <SourcingEventsList />
      </RemountOnReportOpen>
    ), fullWidth: true },
  {
    path: urls.app.marketDialogues.viewAll,
    title: (t) => t("Market dialogues"),
    element: <MarketDialoguesList />,
    fullWidth: true,
  },
  { path: urls.app.marketDialogues.view.pathname, title: (t) => t("Market dialogues"), element: <MarketDialoguePage /> },
  {
    path: urls.app.frameworkAgreements.viewAll,
    title: (t) => t("Framework agreements"),
    element: <FrameworkAgreementsList />,
    fullWidth: true,
  },
  {
    path: urls.app.frameworkAgreements.view.pathname,
    title: (t) => t("Framework agreements"),
    element: <FrameworkAgreementPage />,
  },
  { path: urls.app.kravbibliotek, title: (t) => t("Requirement library"), element: <Kravbibliotek />, fullWidth: true },
  { path: urls.app.sourcingEvents.view.pathname, title: (t) => t("Procurements"), element: <SourcingEventView /> },
  { path: urls.app.sourcingEvents.bidDetail.pathname, title: (t) => t("Bid"), element: <BidDetailView /> },
  { path: urls.app.sourcingEvents.participationRequestDetail.pathname, title: (t) => t("Qualification"), element: <RequestDetailView /> },
  { path: urls.app.sourcingEvents.protocol.pathname, title: (t) => t("Procurement protocol"), element: <ProtocolPage /> },
  { path: urls.app.supplier.procurements, title: (t) => t("Find procurements"), element: <PortalSourcingEventsPage variant="browse" />, fullWidth: true },
  { path: urls.app.supplier.procurement.pathname, title: (t) => t("Find procurements"), element: <PortalSourcingEventPage /> },
  {
    path: urls.app.supplier.marketDialogue.pathname,
    title: (t) => t("Market dialogue"),
    element: <SupplierMarketDialoguePage />,
  },
  { path: urls.app.supplier.bids, title: (t) => t("My bids"), element: <SupplierBidsList />, fullWidth: true },
  {
    path: urls.app.supplier.frameworkAgreements,
    title: (t) => t("My framework agreements"),
    element: <SupplierFrameworkAgreementsList />,
    fullWidth: true,
  },
  { path: urls.app.supplier.bid.edit.paths.intro.pathname, title: (t) => t("Bid"), element: <EditBidPage /> },
  { path: urls.app.supplier.privateBid.edit.pathname, title: (t) => t("Bid"), element: <EditPrivateBidPage /> },
  { path: urls.app.supplier.invitations, title: (t) => t("My invitations"), element: <SupplierInvitationsList />, fullWidth: true },
  { path: "/mine-foresporsler", title: (t) => t("My invitations"), element: <Navigate to={urls.app.supplier.invitations} replace /> },
  { path: urls.app.supplier.request.edit.paths.intro.pathname, title: (t) => t("Request to participate"), element: <EditParticipationRequestPage /> },
  {
    path: urls.app.sourcingEvents.edit.paths.info.pathname, title: (t) => t("Procurements"),
    element: (
      <DraftCard>
        <EditSourcingEventWizard />
      </DraftCard>
    ),
  },
  {
    path: urls.app.sourcingEvents.edit.legacyPathname, title: (t) => t("Procurements"),
    element: <LegacyWizardRedirect />,
  },
  {
    path: urls.app.sourcingEvents.editNegotiationRound.pathname, title: (t) => t("Negotiation round"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditNegotiationRoundWizard />
      </Box>
    ),
  },
  {
    path: urls.app.contracts.viewAll, title: (t) => t("Contracts"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <RemountOnReportOpen>
          <Contracts key="contracts-all" />
        </RemountOnReportOpen>
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.contracts.active, title: (t) => t("Active contracts"),
    element: <ActiveContractsRedirect />,
  },
  {
    path: urls.app.contracts.search, title: (t) => t("Search in contracts"),
    element: <ContractSearchPage />,
    fillHeight: true,
  },
  {
    path: urls.app.contracts.tasks, title: (t) => t("Contract tasks"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <RemountOnReportOpen>
          <ContractTasks />
        </RemountOnReportOpen>
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.contracts.reports, title: (t) => t("My reports"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <MyReports />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.contracts.view.pathname, title: (t) => t("Contracts"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ContractPage />
      </Box>
    ),
  },
  {
    path: urls.app.contracts.edit.pathname, title: (t) => t("Contracts"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditContract />
      </Box>
    ),
  },
  {
    path: urls.app.projects.viewAll, title: (t) => t("Projects"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Projects />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.projects.view.pathname, title: (t) => t("Projects"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ProjectPage />
      </Box>
    ),
  },
  { path: urls.app.profile, title: (t) => t("Profile"), element: <ProfilePage /> },
  { path: urls.app.profileEdit, title: (t) => t("Edit personal profile"), element: <EditProfilePage /> },
  { path: urls.app.settings, title: (t) => t("Settings"), element: <SettingsPage /> },
  { path: urls.app.maler.list, title: (t) => t("Templates"), element: <SourcingEventTemplates />, fullWidth: true },
  { path: urls.app.maler.new.pathname, title: (t) => t("New template"), element: <MalEditor /> },
  { path: urls.app.maler.edit.pathname, title: (t) => t("Templates"), element: <MalEditor /> },
  {
    path: urls.app.users, title: (t) => t("Users"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px="6" pb="6">
        <AccessManagementPage />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.app.organizations, title: (t) => t("Companies"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="2">
        <OrganizationsPage />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: `${urls.app.organizationProfile.pathname}/*`, title: (t) => t("Companies"),
    element: <ViewOrganizationProfile />,
  },
  {
    path: urls.app.organizationProfileEdit, title: (t) => t("Edit company profile"),
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} pb="6">
        <EditOrganizationProfile />
      </Box>
    ),
  },
  ...contributingEntityRoutes,
];

